/**
 * Web-only CircularExposureDial - uses fallback (no react-native-svg)
 * Avoids ESM/CommonJS issues with react-native-svg in webpack
 */

import React from 'react';
import { View, Text, StyleSheet } from 'react-native';

const ICRP_LIMIT_MSV = 6;
const SIZE = 120;

type DialColor = 'green' | 'yellow' | 'red';

function getDialColor(mSv: number): DialColor {
  if (mSv < 2) return 'green';
  if (mSv < 4) return 'yellow';
  return 'red';
}

function getColorHex(color: DialColor): string {
  switch (color) {
    case 'green': return '#4ade80';
    case 'yellow': return '#facc15';
    case 'red': return '#f87171';
    default: return '#4ade80';
  }
}

interface CircularExposureDialProps {
  annualCumulativeMicroSv: number;
  limitMicroSv?: number;
  size?: number;
  fallback?: boolean;
  dark?: boolean;
}

const TEXT_DARK = '#f1f5f9';
const TEXT_MUTED_DARK = '#94a3b8';

export function CircularExposureDial({
  annualCumulativeMicroSv,
  limitMicroSv = ICRP_LIMIT_MSV * 1_000_000,
  size = SIZE,
  dark = false,
}: CircularExposureDialProps) {
  const mSv = annualCumulativeMicroSv / 1_000_000;
  const progress = Math.min(100, (annualCumulativeMicroSv / limitMicroSv) * 100);
  const color = getDialColor(mSv);
  const a11yLabel = `Annual dose progress: ${progress.toFixed(0)}%. ${mSv.toFixed(2)} millisieverts of ${limitMicroSv / 1_000_000} mSv limit.`;

  return (
    <View
      style={[styles.fallbackContainer, { width: size, height: size }]}
      accessibilityLabel={a11yLabel}
      accessibilityRole="progressbar"
      accessibilityValue={{ min: 0, max: 100, now: progress }}
    >
      <View style={[styles.fallbackRing, { width: size, height: size, borderColor: getColorHex(color) }]}>
        <Text style={[styles.fallbackValue, dark && { color: TEXT_DARK }]}>{(mSv).toFixed(2)}</Text>
        <Text style={[styles.fallbackUnit, dark && { color: TEXT_MUTED_DARK }]}>mSv</Text>
      </View>
      <Text style={[styles.fallbackProgress, dark && { color: TEXT_MUTED_DARK }]}>{progress.toFixed(0)}% of limit</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  fallbackContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  fallbackRing: {
    borderRadius: 999,
    borderWidth: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  fallbackValue: {
    fontSize: 20,
    fontWeight: '700',
    color: '#0f172a',
  },
  fallbackUnit: {
    fontSize: 11,
    color: '#475569',
  },
  fallbackProgress: {
    fontSize: 10,
    color: '#475569',
    marginTop: 4,
  },
});
